// Create a new custom element for product links with images for transitions to PDP
class ProductCardLink extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleCardClick);
    this.addEventListener('click', this.#handleSwatchClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleCardClick);
    this.removeEventListener('click', this.#handleSwatchClick);
  }

  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for the product link
   * @param {Event} event
   */
  #handleCardClick = (event) => {
    setTimeout(() => this.#handleClick(event), 0);
  };

  /**
   * Handles the click event for the product link
   * @param {Event} event
   */
  #handleClick = (event) => {
    // If the event has been prevented, don't do anything, another component is handling the click or if it's an input (swatches)
    if (event.defaultPrevented || event.target instanceof HTMLInputElement) return;

    const gallery = this.querySelector('[data-view-transition-to-main-product]');
    if (!this.productTransitionEnabled || !(gallery instanceof HTMLElement)) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const activeImage =
      gallery.querySelector('slideshow-slide[aria-hidden="false"] [transitionToProduct="true"]') ||
      gallery.querySelector('[transitionToProduct="true"]:last-child');

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    gallery.setAttribute('data-view-transition-type', 'product-image-transition');
    gallery.setAttribute('data-view-transition-triggered', 'true');
  };

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (this.featuredMediaUrl && !image.srcset.includes(this.featuredMediaUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }

  #handleSwatchClick = (event) => {
    const swatchLink = event.target instanceof Element ? event.target.closest('[data-variant-swatch-link]') : null;
    if (!(swatchLink instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopPropagation();
    this.#applyVariantState(swatchLink);
  };

  #applyVariantState(swatchLink) {
    const variantId = swatchLink.dataset.variantId ?? '';
    const variantUrl = swatchLink.dataset.variantUrl ?? '';
    const variantImageSrc = swatchLink.dataset.variantImageSrc ?? '';
    const variantImageSrcset = swatchLink.dataset.variantImageSrcset ?? '';
    const featuredMediaUrl = swatchLink.dataset.featuredMediaUrl ?? '';
    const productCard = swatchLink.closest('product-card');

    if (productCard instanceof HTMLElement && variantId) {
      productCard.dataset.variantId = variantId;
    }

    this.setAttribute('data-featured-media-url', featuredMediaUrl);

    this.querySelectorAll('[data-variant-swatch-link]').forEach((link) => {
      link.classList.toggle('product-card__variant-swatch--active', link === swatchLink);
    });

    this.querySelectorAll('.card-gallery__link, .product-title-link').forEach((link) => {
      if (link instanceof HTMLAnchorElement && variantUrl) {
        link.href = variantUrl;
      }
    });

    this.querySelectorAll('[data-variant-more-link]').forEach((link) => {
      if (link instanceof HTMLAnchorElement && variantUrl) {
        link.href = variantUrl;
      }
    });

    if (productCard instanceof HTMLElement && variantId) {
      productCard.querySelectorAll('.quick-add-button-wrapper').forEach((wrapper) => {
        if (!(wrapper instanceof HTMLElement)) return;

        wrapper.dataset.variantId = variantId;
        wrapper.dataset.variantid = variantId;

        const inputId = wrapper.querySelector('input[name="id"]');
        if (inputId instanceof HTMLInputElement) {
          inputId.value = variantId;
        }

        const reserveEligible = swatchLink.dataset.variantReserveEligible === 'true';
        const cartMode = wrapper.querySelector('.quick-add-button-mode--cart');
        const reserveMode = wrapper.querySelector('.quick-add-button-mode--reserve');
        if (cartMode instanceof HTMLElement) {
          cartMode.hidden = reserveEligible;
        }
        if (reserveMode instanceof HTMLElement) {
          reserveMode.hidden = !reserveEligible;
        }

        const quickAddButton = wrapper.querySelector('[data-quick-add-button]');
        if (quickAddButton && quickAddButton._qa) {
          quickAddButton._qa.variantId = variantId;
          quickAddButton._qa.sync();
        }

        const reserveButton = wrapper.querySelector('[data-reserve-product]');
        if (reserveButton instanceof HTMLElement) {
          reserveButton.dataset.variantId = variantId;
          reserveButton.setAttribute('data-variant-id', variantId);

          const productPath = swatchLink.dataset.productPath ?? '';
          if (productPath) {
            reserveButton.dataset.productPath = productPath;
            reserveButton.setAttribute('data-product-path', productPath);
          }

          if (variantUrl) {
            reserveButton.dataset.productUrl = variantUrl;
            reserveButton.setAttribute('data-product-url', variantUrl);
          } else if (productPath && variantId) {
            const normalizedPath = productPath.startsWith('/') ? productPath : `/${productPath}`;
            const builtUrl = `${window.location.origin}${normalizedPath}?variant=${variantId}`;
            reserveButton.dataset.productUrl = builtUrl;
            reserveButton.setAttribute('data-product-url', builtUrl);
          }

          const variantSku = swatchLink.dataset.variantSku ?? '';
          reserveButton.dataset.variantSku = variantSku;
          reserveButton.setAttribute('data-variant-sku', variantSku);

          if (variantImageSrc) {
            reserveButton.dataset.productImage = variantImageSrc;
            reserveButton.setAttribute('data-product-image', variantImageSrc);
          }
        }
      });
    }

    const gallery = this.querySelector('[data-view-transition-to-main-product]');
    const activeImage =
      gallery?.querySelector('slideshow-slide[aria-hidden="false"] [transitionToProduct="true"]') ||
      gallery?.querySelector('[transitionToProduct="true"]:last-child');

    if (activeImage instanceof HTMLImageElement) {
      if (variantImageSrc) activeImage.src = variantImageSrc;
      if (variantImageSrcset) activeImage.srcset = variantImageSrcset;
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}
